import {
	PricingType,
	SanitizedWarrantySelection,
	SelectableWarranty,
} from "@dune/types";
import {
	Checkbox,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@dune/ui";
import { getLimitTypeDisplay } from "@dune/vars";
import { usePricingOptions } from "./contexts/PricingOptionsContext";
import { match, P } from "ts-pattern";
import { useEffect, useMemo, useState } from "react";
import { useGetPricingByConstructionSiteAndBinderWarranty } from "../../../api/queries/constructionSiteOfferPricings.queries";
import { PriceInput } from "../../../components/inputs/PriceInput";

const shouldDisplayWarranty = (
	warranty: SelectableWarranty,
	isEditMode: boolean,
): boolean => {
	if (warranty.isMandatory || !warranty.isAvailable) {
		return false;
	}

	return isEditMode || warranty.checked;
};

const BinderWarrantyOfferOptionsContainer = ({
	binderWarranty,
	handleDependentWarrantyCheckboxChange,
	handleBinderWarrantyWarrantyCheckboxChange,
	handleBinderWarrantyWarrantyLimitChange,
	handleBrokerFeesChange,
	storedPricingType,
	constructionSiteId,
	isDisabled = false,
}: {
	binderWarranty: SanitizedWarrantySelection;
	handleDependentWarrantyCheckboxChange?: (
		baseWarrantyId: number,
		id: number,
	) => void;
	handleBinderWarrantyWarrantyCheckboxChange?: (
		baseWarrantyId: number,
		id: number,
	) => void;
	handleBinderWarrantyWarrantyLimitChange?: (
		baseWarrantyId: number,
		warrantyId: number,
		limitOptionId: number,
	) => void;
	handleBrokerFeesChange?: (baseWarrantyId: number, brokerFees: number) => void;
	storedPricingType?: PricingType;
	constructionSiteId: string;
	isDisabled?: boolean;
}) => {
	const {
		updateBrokerFees,
		updateCnrContractorState,
		getPricingOptionsForWarranty,
		updateExistingDamagesChecked,
		updateExistingDamagesRate,
		pricingOptions,
	} = usePricingOptions();
	const currentBrokerFees = getPricingOptionsForWarranty(
		binderWarranty.id,
	).brokerFees;

	const currentCnrContractorState = getPricingOptionsForWarranty(
		binderWarranty.id,
	).isNonPerformingContractor;

	const existingDamagesWarranty = binderWarranty.warranties.find(
		(warranty) => warranty.shortName === "Dommages aux existants",
	);
	const currentExistingDamagesChecked =
		pricingOptions.existingDamagesChecked[binderWarranty.id] ?? false;
	const currentExistingDamagesRate =
		pricingOptions.existingDamagesRates[binderWarranty.id] ?? 0;

	const useStoredPricing = useMemo(
		() => !!storedPricingType,
		[storedPricingType],
	);

	const { data: constructionSiteOfferPricingResult } =
		useGetPricingByConstructionSiteAndBinderWarranty(
			Number(constructionSiteId),
			binderWarranty.id,
			storedPricingType,
		);

	const { intermediaryManagementFeesTTC } = useMemo(
		() =>
			constructionSiteOfferPricingResult ?? {
				intermediaryManagementFeesTTC: 0,
			},
		[constructionSiteOfferPricingResult],
	);

	const isReadOnly = isDisabled || useStoredPricing;

	useEffect(() => {
		const nextBrokerFees = useStoredPricing
			? (intermediaryManagementFeesTTC ?? 0)
			: binderWarranty.brokerFees;

		if (nextBrokerFees && nextBrokerFees !== currentBrokerFees) {
			updateBrokerFees(binderWarranty.id, nextBrokerFees);
		}
		const cnrBinderDependentWarranty = binderWarranty.dependentWarranties?.find(
			(warranty) => warranty.warrantyType === "CNR",
		);
		if (
			cnrBinderDependentWarranty &&
			cnrBinderDependentWarranty.checked !== currentCnrContractorState
		) {
			updateCnrContractorState(
				binderWarranty.id,
				cnrBinderDependentWarranty?.checked ?? false,
			);
		}

		if (existingDamagesWarranty) {
			const existingDamagesChecked = existingDamagesWarranty.checked ?? false;

			if (existingDamagesChecked !== currentExistingDamagesChecked) {
				updateExistingDamagesChecked(binderWarranty.id, existingDamagesChecked);
			}

			if (
				existingDamagesChecked &&
				existingDamagesWarranty.selectedLimitOptionId
			) {
				const selectedOption =
					existingDamagesWarranty.warrantyLimitOptions?.find(
						(option) =>
							option.limitOption.id ===
							existingDamagesWarranty.selectedLimitOptionId,
					);
				if (
					selectedOption?.limitOption.value &&
					(existingDamagesWarranty.warrantyLimit?.limitType === "PERCENTAGE" ||
						existingDamagesWarranty.warrantyLimit?.limitType ===
							"CONSTRUCTION_COST_PERCENTAGE")
				) {
					const expectedRate = selectedOption.limitOption.value / 100;
					if (currentExistingDamagesRate !== expectedRate) {
						updateExistingDamagesRate(
							binderWarranty.id,
							selectedOption.limitOption.value,
						);
					}
				}
			} else if (!existingDamagesChecked && currentExistingDamagesRate > 0) {
				updateExistingDamagesRate(binderWarranty.id, 0);
			}
		}
	}, [
		useStoredPricing,
		intermediaryManagementFeesTTC,
		binderWarranty,
		currentBrokerFees,
		currentCnrContractorState,
		updateBrokerFees,
		updateCnrContractorState,
		existingDamagesWarranty,
		currentExistingDamagesChecked,
		currentExistingDamagesRate,
		updateExistingDamagesChecked,
		updateExistingDamagesRate,
	]);

	return (
		<div
			className={
				"border-solid border-1 border-[#038EB7] rounded-lg p-4 space-y-4 relative"
			}
		>
			<h1 className="font-medium text-md text-[#144C65]">AJOUTEZ</h1>
			<div className="flex flex-row items-center">
				<h1 className="w-full">Frais apporteurs</h1>
				<PriceInput
					iconOrText="€"
					min={0}
					value={currentBrokerFees}
					onChange={(value) => {
						const numericValue = value === "" ? 0 : Number(value);
						handleBrokerFeesChange?.(binderWarranty.id, numericValue);
						updateBrokerFees(binderWarranty.id, numericValue);
					}}
					readOnly={!handleBrokerFeesChange}
					disabled={isReadOnly}
				/>
			</div>
			{binderWarranty.dependentWarranties
				?.filter((dependentWarranty) => !dependentWarranty.isMandatory)
				.map((dependentWarranty) => {
					return (
						<Checkbox
							key={dependentWarranty.id}
							id={`${String(dependentWarranty.id)}-${dependentWarranty.name}`}
							label={`La garantie ${dependentWarranty.shortName ?? dependentWarranty.name} (${dependentWarranty.warrantyType})`}
							checked={dependentWarranty.checked}
							onCheckedChange={() => {
								handleDependentWarrantyCheckboxChange?.(
									binderWarranty.id,
									dependentWarranty.id,
								);
								if (dependentWarranty.warrantyType === "CNR") {
									updateCnrContractorState(
										binderWarranty.id,
										!dependentWarranty.checked,
									);
								}
							}}
							disabled={!handleDependentWarrantyCheckboxChange || isReadOnly}
							className="border-gray-300 disabled:bg-gray-100"
						/>
					);
				})}
			{binderWarranty.warranties
				.filter((warranty) =>
					shouldDisplayWarranty(
						warranty,
						!!handleBinderWarrantyWarrantyCheckboxChange,
					),
				)
				.map((warranty) => {
					return (
						<SelectableAvailableWarrantiesContainer
							key={warranty.id}
							warranty={warranty}
							handleBinderWarrantyWarrantyCheckboxChange={
								handleBinderWarrantyWarrantyCheckboxChange
							}
							handleBinderWarrantyWarrantyLimitChange={
								handleBinderWarrantyWarrantyLimitChange
							}
							binderWarranty={binderWarranty}
							isDisabled={isReadOnly}
						/>
					);
				})}
		</div>
	);
};

const SelectableAvailableWarrantiesContainer = ({
	warranty,
	binderWarranty,
	handleBinderWarrantyWarrantyCheckboxChange,
	handleBinderWarrantyWarrantyLimitChange,
	isDisabled = false,
}: {
	warranty: SelectableWarranty;
	binderWarranty: SanitizedWarrantySelection;
	handleBinderWarrantyWarrantyCheckboxChange?: (
		baseWarrantyId: number,
		id: number,
	) => void;
	handleBinderWarrantyWarrantyLimitChange?: (
		baseWarrantyId: number,
		warrantyId: number,
		limitOptionId: number,
	) => void;
	isDisabled?: boolean;
}) => {
	const { updateExistingDamagesRate, updateExistingDamagesChecked } =
		usePricingOptions();

	// State to track the current select value
	const [currentSelectValue, setCurrentSelectValue] = useState<string>(
		warranty.selectedLimitOptionId?.toString() ??
			warranty.warrantyLimit?.id.toString() ??
			"",
	);

	return match(warranty)
		.with(
			{ warrantyLimit: P.nonNullable, isValueSelectable: true },
			(warranty) => (
				<div className="flex flex-row justify-between items-center">
					<Checkbox
						id={`${String(warranty.id)}-${warranty.name}`}
						className="border-gray-300 disabled:bg-gray-100"
						label={`La garantie ${warranty.shortName ?? warranty.name}`}
						checked={warranty.checked}
						disabled={!handleBinderWarrantyWarrantyCheckboxChange || isDisabled}
						onCheckedChange={() => {
							handleBinderWarrantyWarrantyCheckboxChange?.(
								binderWarranty.id,
								warranty.id,
							);

							// Handle existing damages checkbox state
							if (warranty.shortName === "Dommages aux existants") {
								updateExistingDamagesChecked(
									binderWarranty.id,
									!warranty.checked,
								);

								if (warranty.checked) {
									// Checkbox will be unchecked: reset damage rate to 0
									updateExistingDamagesRate(binderWarranty.id, 0);
								} else {
									// Checkbox will be checked: use current displayed value
									const currentOption = warranty.warrantyLimitOptions.find(
										(option) =>
											option.limitOption.id.toString() === currentSelectValue,
									);
									const valueToRestore = currentOption?.limitOption.value ?? 0;
									updateExistingDamagesRate(binderWarranty.id, valueToRestore);
								}
							}
						}}
					/>
					<div className="ml-2">
						<Select
							value={currentSelectValue}
							disabled={!warranty.checked || isDisabled}
							onValueChange={(value) => {
								// Always update the current select value for display
								setCurrentSelectValue(value);

								// Only update the actual warranty data if checkbox is checked
								if (warranty.checked) {
									handleBinderWarrantyWarrantyLimitChange?.(
										binderWarranty.id,
										warranty.id,
										Number(value),
									);

									if (
										warranty.shortName === "Dommages aux existants" &&
										(warranty.warrantyLimit.limitType === "PERCENTAGE" ||
											warranty.warrantyLimit.limitType ===
												"CONSTRUCTION_COST_PERCENTAGE")
									) {
										const selectedOption = warranty.warrantyLimitOptions.find(
											(option) => option.limitOption.id.toString() === value,
										);
										if (selectedOption?.limitOption.value) {
											updateExistingDamagesRate(
												binderWarranty.id,
												selectedOption.limitOption.value,
											);
										}
									}
								}
							}}
						>
							<SelectTrigger
								size="default"
								className="disabled:opacity-50"
								disabled={
									!handleBinderWarrantyWarrantyLimitChange || isDisabled
								}
							>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{warranty.warrantyLimitOptions
									.filter(
										(limitOption) => limitOption.limitOption.value !== null,
									)
									.map((option) => (
										<SelectItem
											value={option.limitOption.id.toString()}
											key={option.limitOption.id}
										>
											{`${option.limitOption.value}${getLimitTypeDisplay(option.limitOption.limitType)}`}
										</SelectItem>
									))}
							</SelectContent>
						</Select>
					</div>
				</div>
			),
		)
		.otherwise(() => undefined);
};
export default BinderWarrantyOfferOptionsContainer;
